import {
  anyContainingPermission,
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { buildPermission, getCustomerId } from "common/utils/auth";
import {
  Button,
  capitalize,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Typography,
} from "@mui/material";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { EditEquipmentButton } from "./EditEquipmentButton";
import { EmptyGarage } from "./EmptyGarage";
import { EquipmentLibraryCard } from "portal/components/atk/EquipmentLibraryCard";
import { EquipmentSubpath, Path } from "portal/utils/routing";
import { getImplementLibraryCardData } from "portal/components/atk/ImplementLibraryCardData";
import { Header } from "portal/components/header/Header";
import { Implement } from "protos/atk/autotractor";
import { Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DetailsIcon from "@mui/icons-material/MoreHoriz";
import React, { FC, useState } from "react";

interface ImplementLibraryProps {}

export const _ImplementLibraryPage: FC<ImplementLibraryProps> = () => {
  const { t } = useTranslation();
  const { user } = useSelf();
  const selfCustomerId = getCustomerId(user);
  const [customerId, setCustomerId] = useState(selfCustomerId);

  const canSwitchCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  const canCreateOrEditArchetypes = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  // XXX: customer access to create implements is temporarily disabled until
  // this flow gets a UX pass per NickT.
  const canCreateImplement = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  const navigate = useNavigate();

  const { data: searchResult, isFetching: implementsFetching } = useQueryPopups(
    atkEquipmentApi.endpoints.searchImplements.useQuery(
      {
        customerId: customerId?.toString(),
      },
      { skip: !customerId }
    )
  );

  let libraryBody: JSX.Element | undefined;
  const [detailsMenu, setDetailsMenu] = useState<
    { anchorEl: Element; implement: Implement } | undefined
  >();
  const [showDeleteModal, setShowDeleteModal] = useState<
    Implement | undefined
  >();

  const [deleteImplement] = useMutationPopups(
    atkEquipmentApi.endpoints.deleteImplement.useMutation(),
    {
      success: capitalize(
        t("utils.actions.deletedLong", {
          subject: t("models.equipment.implement", { count: 1 }),
        })
      ),
    }
  );

  if (implementsFetching) {
    libraryBody = (
      <div className="w-full mt-20 flex flex-row gap-4 justify-center items-center">
        <CircularProgress variant="indeterminate" />
        <Typography variant="h5">
          {t("components.Loading.placeholder")}
        </Typography>
      </div>
    );
  } else if (searchResult?.implements.length) {
    libraryBody = (
      <div className="grid grid-flow-row gap-4 grid-cols-2">
        {searchResult.implements.map((implement) => (
          <EquipmentLibraryCard
            key={implement.id}
            data={getImplementLibraryCardData(t, implement)}
            ActionButtonIcon={DetailsIcon}
            onActionButtonClick={(e) =>
              setDetailsMenu({ anchorEl: e.currentTarget, implement })
            }
          />
        ))}
        <Menu
          open={detailsMenu !== undefined}
          anchorEl={detailsMenu?.anchorEl}
          onClose={() => setDetailsMenu(undefined)}
        >
          <MenuList>
            <EditEquipmentButton
              variant="menuItem"
              type="implement"
              equipmentId={detailsMenu?.implement.id ?? ""}
            />
            {canCreateOrEditArchetypes && detailsMenu?.implement.archetype && (
              <EditEquipmentButton
                variant="menuItem"
                type="implement-archetype"
                equipmentId={detailsMenu.implement.archetype.id}
              />
            )}
            <MenuItem
              onClick={() => setShowDeleteModal(detailsMenu?.implement)}
            >
              <ListItemIcon>
                <DeleteForeverIcon />
              </ListItemIcon>
              <ListItemText>{t("utils.actions.delete")}</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
        {showDeleteModal && (
          <ConfirmationDialog
            destructive
            yesText={t("utils.actions.deleteLong", {
              subject: t("models.equipment.implement", { count: 1 }),
            })}
            description={t("components.ConfirmationDialog.delete.description", {
              subject: showDeleteModal.name,
            })}
            onYes={async () => {
              const res = await deleteImplement(showDeleteModal);
              if (!res.error) {
                setShowDeleteModal(undefined);
                setDetailsMenu(undefined);
              }
            }}
            onClose={() => setShowDeleteModal(undefined)}
          />
        )}
      </div>
    );
  } else {
    libraryBody = <EmptyGarage variant="implements" />;
  }

  return (
    <Page>
      <Header
        title={t("views.autotractor.equipment.implementLibrary")}
        parentLink={Path.EQUIPMENT}
      />
      <div className="flex flex-col gap-4">
        <div className="flex">
          <Typography variant="h4" className="self-center">
            {t("views.autotractor.equipment.implementLibrary")}
          </Typography>
          <div className="flex-1" />
          {canSwitchCustomers && (
            <CustomerSelector
              value={customerId}
              onChange={setCustomerId}
              className="w-1/5 mx-4"
            />
          )}
          <div className="flex gap-2">
            {canCreateImplement && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() =>
                  navigate(
                    `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_IMPLEMENT}`
                  )
                }
              >
                {t("models.equipment.implement", { count: 1 })}
              </Button>
            )}
            {canCreateOrEditArchetypes && (
              <Button
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_IMPLEMENT_ARCHETYPE}`
                  )
                }
              >
                {t(
                  "views.autotractor.equipment.implements.newImplementArchetype"
                )}
              </Button>
            )}
          </div>
        </div>
        {libraryBody}
      </div>
    </Page>
  );
};

export const ImplementLibraryPage = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.all
      ),
    ],
  },
  _ImplementLibraryPage,
  <UnauthorizedPage />
);
